import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@apollo/client';
import {
  IAllowedDomainSettingEditMutation,
  IAllowedDomainSettingEditMutationVariables,
  PermissionKey,
} from '../../../../utils/apollo/graphqlSchema';
import { assertNotNil } from '../../../../utils/types';
import { EDIT_ALLOWED_DOMAIN_SETTING } from '../../../../utils/apollo/globalConfig';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import usePermission from '../../../../utils/hooks/permissionHooks';
import toastMessage from '../../../../utils/toastMessage';
import styled from 'styled-components';
import { PrimaryButton } from '../../../../components/Button';
import {
  FieldLabel,
  InputField,
  VerticalField,
} from '../../../../components/Form';
import { z } from 'zod';
import { theme } from '../../../../styles/theme';
import {
  IAllowedDomainSettingFormState,
  allowedDomainSettingSchema,
} from './types';
import { colors } from '../../../../config/styles';

const InputWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  width: 320px;
`;

const ErrorMessage = styled.div`
  font-size: 12px;
  color: ${colors.mainError};
  margin-top: 4px;
`;

const HelpText = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${theme.colors.secondary600};
  margin-top: 4px;
`;

const AllowedDomainSettingForm = ({
  allowedDomains,
  refetch,
}: {
  allowedDomains: string[];
  refetch: () => void;
}) => {
  const { checkIsPermitted } = usePermission();
  const isPermittedToEdit = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.AdminSettingEdit],
  });

  const [updateAllowedDomainSetting] = useMutation<
    IAllowedDomainSettingEditMutation,
    IAllowedDomainSettingEditMutationVariables
  >(EDIT_ALLOWED_DOMAIN_SETTING);

  const formMethods = useForm<IAllowedDomainSettingFormState>({
    resolver: zodResolver(allowedDomainSettingSchema),
    defaultValues: {
      newDomainInput: '',
    },
    mode: 'onSubmit',
    disabled: !isPermittedToEdit,
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, disabled },
    reset,
  } = formMethods;

  const onSubmit = async (formData: IAllowedDomainSettingFormState) => {
    try {
      const newDomain = formData.newDomainInput.trim();

      // Check if domain already exists
      if (allowedDomains.includes(newDomain)) {
        toastMessage({
          message: 'This domain is already in the allowed list.',
          type: 'error',
        });
        return;
      }

      const validationResult = allowedDomainSettingSchema.safeParse({
        newDomainInput: newDomain,
      });
      if (!validationResult.success) {
        toastMessage({
          message: validationResult.error.errors[0].message,
          type: 'error',
        });
        return;
      }

      const updatedDomains = [...allowedDomains, newDomain];

      const response = await updateAllowedDomainSetting({
        variables: {
          input: {
            allowedDomains: updatedDomains,
          },
        },
      });

      if (
        printGraphqlErrors(response?.data?.allowedDomainSettingEdit?.errors)
      ) {
        return;
      }

      const entity = response?.data?.allowedDomainSettingEdit?.entity;
      assertNotNil(entity);

      toastMessage({ message: 'Domain added successfully.', type: 'info' });
      reset({ newDomainInput: '' });
      refetch();
    } catch (error) {
      const message =
        error instanceof z.ZodError
          ? `Invalid domain format: ${error.errors
              .map((err) => err.message)
              .join(', ')}`
          : error instanceof Error
            ? error.message
            : 'An unexpected error occurred';

      toastMessage({ message, type: 'error' });
    }
  };

  const hasError = !!formMethods.formState.errors.newDomainInput;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VerticalField>
        <FieldLabel>Add domain</FieldLabel>
        <InputWrapper>
          <Controller
            name="newDomainInput"
            control={control}
            render={({ field, fieldState }) => (
              <InputField
                {...field}
                value={field.value || ''}
                onChange={(value: string) => field.onChange(value)}
                placeholder="domain.com"
                hasError={!!fieldState.error}
                disabled={disabled}
                style={{ flex: 1 }}
              />
            )}
          />
          <PrimaryButton
            type="submit"
            disabled={
              hasError ||
              isSubmitting ||
              disabled ||
              !formMethods.watch('newDomainInput').trim()
            }
            trackContext="allowed domains"
            trackAction="add"
          >
            Submit
          </PrimaryButton>
        </InputWrapper>
        {hasError ? (
          <ErrorMessage>
            {formMethods.formState.errors.newDomainInput?.message}
          </ErrorMessage>
        ) : (
          <HelpText>Format: domain.com</HelpText>
        )}
      </VerticalField>
    </form>
  );
};

export default AllowedDomainSettingForm;
